import { z } from 'zod'
import { note300, uuid } from './primitives.ts'

/** M16-D: bunkers as readings (benchmark 37, GK-032; docs/M16.md D). A sand-depth walk is a set of readings of the notebook (M13): the two types
 *  `sand_depth_floor` / `sand_depth_face` (measurements.ts, the twin of ref.measurement_type), each reading carrying the club's own name of a cell of its grid
 *  ("A2", "B1") as `position_label`. `measurement.record@2` = @1's payload plus that optional label: a FIELD FACT under measurement.record (offline-first,
 *  projected by the measurement projector of sync/projectors.ts, which reads both versions), kiosk-allowed (rule 9: the shared workstation sends it under
 *  the person's PIN); @1 stays as shipped. The schema mirrors `core.command_type.payload_schema` in `20261210100200_sand_depth.sql`, which derives the stored
 *  @2 schema from the stored @1 one (an asserted jsonb_set); `measurementsV2.test.ts` holds this twin to `commands.ts`'s @1 on the fixtures, and the contract
 *  check (ops/contracts/check.mjs) runs the same fixtures and random payloads through both. A part's file imports primitives.ts, never commands.ts. */

/** The label's bound, in code points (the schema's maxLength, the column's CHECK, the form's cap). */
export const POSITION_LABEL_MAX = 16

// the building blocks of @1, as commands.ts defines them (docs/M16.md build notes: a part's file never imports commands.ts)
const instant = z.string().regex(/^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9:.]+(Z|[+-][0-9]{2}:?[0-9]{2})$/)
const measType = z.string().regex(/^[a-z][a-z0-9_]{1,40}$/)
const text80 = z.string().max(80).nullable()
const depthMm = z.number().int().min(0).max(2000).nullable()
const unitText = z.string().min(1).max(20)
const uuidOrNull = uuid.nullable()

/** A reading with the cell of the club's grid it was taken in (@1's fields, plus the label). */
export const measurementRecordV2 = z.strictObject({
  measurement_id: uuid, measurement_type: measType, area_id: uuidOrNull.optional(), value: z.number(), unit: unitText, observed_at: instant.optional(),
  depth_mm: depthMm.optional(), method: text80.optional(), instrument: text80.optional(), note: note300.optional(),
  position_label: z.string().max(POSITION_LABEL_MAX).nullable().optional(),
})
export type MeasurementRecordV2 = z.infer<typeof measurementRecordV2>

/** The label as the server stores it: trimmed of spaces (Postgres' btrim), and a label of spaces is no label. */
export const positionLabelOf = (label: string | null | undefined): string | null => {
  const t = (label ?? '').replace(/^ +| +$/g, '')
  return t === '' ? null : t
}
/** The twin of the handler's own rule on the cell, judged AFTER every check of @1 (prepareReading): a label names a place within an area, so a reading that names
 *  no area takes none; and at most 16 code points (the schema's bound, counted as the server counts: code points, not bytes). */
export function positionLabelProblem(label: string | null | undefined, hasArea: boolean): 'MEASUREMENT_POSITION_INVALID' | null {
  const l = positionLabelOf(label)
  if (l === null) return null
  return !hasArea || [...l].length > POSITION_LABEL_MAX ? 'MEASUREMENT_POSITION_INVALID' : null
}
